import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageOff, Loader2, Package, Plus, RefreshCw, Search, Tags, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { Product } from '@/lib/types';

// Miniaturka produktu z feeda. Pole `image` bywa puste (wszystkie pola poza `id`
// mają domyślny pusty string), a URL z feeda potrafi zwrócić 404 — w obu
// przypadkach pokazujemy placeholder zamiast zepsutej ikony przeglądarki.
function ProductThumb({ src, alt }: { src: string; alt: string }) {
	const [broken, setBroken] = useState(false);
	if (!src || broken) {
		return (
			<div className="flex size-20 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
				<ImageOff className="size-5" />
			</div>
		);
	}
	return (
		<img
			src={src}
			alt={alt}
			loading="lazy"
			onError={() => setBroken(true)}
			className="size-20 shrink-0 rounded-md border border-border bg-white object-contain"
		/>
	);
}

// `moto` — osobna lista motoryzacyjna dla autobloga (bez tagów i odświeżania feeda).
export default function ProductsPage({ moto = false }: { moto?: boolean }) {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState('');
	const [newId, setNewId] = useState('');
	const source = moto ? api.motoProducts : api.products;
	const listKey = moto ? 'moto-products' : 'products';

	const productsQuery = useQuery({
		queryKey: [listKey, search],
		queryFn: () => source.list(search),
	});

	const addProductMutation = useMutation({
		mutationFn: (id: string) => source.add(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [listKey] });
			setNewId('');
		},
	});

	const removeProductMutation = useMutation({
		mutationFn: (id: string) => source.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [listKey] });
		},
	});

	const refreshMutation = useMutation({
		mutationFn: () => api.products.refresh(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['products'] });
		},
	});

	return (
		<div className="flex flex-col gap-6 p-6">
			<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
				<div>
					<div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
						<Package className="size-4" />
						Feed produktów
					</div>
					<h1 className="text-2xl font-semibold">{moto ? 'Produkty moto' : 'Edycja produktów'}</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						{moto
							? 'Osobna lista dla autobloga: widget pokazuje 2 produkty stąd i 2 z ogólnej listy.'
							: 'Dodawaj produkty z feeda Mediaexpert, przeglądaj zdjęcia i usuwaj nieaktualne.'}
					</p>
				</div>

				<div className={moto ? 'hidden' : 'flex items-center gap-2'}>
					<Link
						to="/feed/tags"
						className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium transition hover:bg-accent"
					>
						<Tags className="size-4" />
						Mapowanie tagów
					</Link>
					<button
						type="button"
						onClick={() => refreshMutation.mutate()}
						className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium transition hover:bg-accent"
						disabled={refreshMutation.isPending}
					>
						{refreshMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
						Odśwież feed
					</button>
				</div>
			</div>

			<section className="rounded-xl border border-border bg-card p-4 shadow-sm">
				<div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
					<div>
						<h2 className="text-lg font-semibold">Produkty</h2>
						<p className="text-sm text-muted-foreground">Wyszukaj i zarządzaj aktualnie zindeksowanymi produktami.</p>
					</div>
					<div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 md:w-72">
						<Search className="size-4 text-muted-foreground" />
						<input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Szukaj po nazwie lub ID"
							className="w-full bg-transparent text-sm outline-none"
						/>
					</div>
				</div>

				<div className="mb-4 flex flex-col gap-2 md:flex-row">
					<input
						value={newId}
						onChange={(e) => setNewId(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && newId.trim() && addProductMutation.mutate(newId.trim())}
						placeholder="ID produktu z feeda"
						className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none"
					/>
					<button
						type="button"
						onClick={() => newId.trim() && addProductMutation.mutate(newId.trim())}
						className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
						disabled={addProductMutation.isPending}
					>
						{addProductMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
						Dodaj
					</button>
				</div>

				{addProductMutation.isError ? (
					<p className="mb-3 text-sm text-destructive">
						{(addProductMutation.error as Error)?.message ?? 'Nie udało się dodać produktu.'}
					</p>
				) : null}

				{productsQuery.isLoading ? (
					<div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
						<Loader2 className="mr-2 size-4 animate-spin" />
						Ładowanie produktów…
					</div>
				) : productsQuery.data?.length ? (
					<div className="grid gap-2 sm:grid-cols-2">
						{productsQuery.data.map((product: Product) => (
							<div key={product.id} className="flex gap-3 rounded-lg border border-border p-3">
								<ProductThumb src={product.image} alt={product.name || product.id} />
								<div className="flex min-w-0 flex-1 flex-col">
									<a
										href={product.url}
										target="_blank"
										rel="noreferrer"
										className="line-clamp-2 font-medium text-primary hover:underline"
									>
										{product.name || product.id}
									</a>
									<span className="mt-1 w-fit rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{product.id}</span>
									<p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
										{product.category || 'Brak kategorii'} · {product.availability || 'Brak dostępności'}
									</p>
									<div className="mt-auto flex items-center justify-between pt-2">
										<span className="text-sm font-medium">{product.price || product.omnibus_price || 'Brak ceny'}</span>
										<button
											type="button"
											onClick={() => removeProductMutation.mutate(product.id)}
											className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm transition hover:bg-accent"
											disabled={removeProductMutation.isPending}
										>
											<Trash2 className="size-4" />
											Usuń
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
						Brak produktów.
					</div>
				)}
			</section>
		</div>
	);
}
